import { profileStartup } from '../diagnostics/startup-profile';
import { beginStartupWork, finishStartupWork } from '../diagnostics/startup-work';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconPhoto, IconRefresh } from '../ui/icons';
import { bucketOf, cardUrl, hostsFor, nextAttempt, photoUrl } from './photo';

/**
 * Фотография товара с увеличением.
 *
 * УВЕЛИЧЕНИЕ РИСУЕТСЯ В КОРНЕ СТРАНИЦЫ, А НЕ РЯДОМ С КАРТИНКОЙ. Это
 * исправление, и вот в чём была ошибка: увеличение лежало внутри строки,
 * а строка — внутри списка с прокруткой. Всё, что выходит за край такого
 * родителя, обрезается — и увеличение не появлялось вовсе. Никакой
 * «почти работает» здесь не бывает: либо рисуем поверх всей страницы,
 * либо не рисуем.
 *
 * ПОЛОЖЕНИЕ СЧИТАЕТСЯ ОТ САМОЙ КАРТИНКИ и разворачивается влево у правого
 * края экрана: увеличение, вылезшее за окно, ничем не лучше обрезанного.
 *
 * УВЕЛИЧЕНИЕ ПО НАВЕДЕНИЮ, а не по щелчку. Картинка в строке размером
 * с ноготь: разглядеть на ней мелкий товар — серьги, подвеску, шрифт
 * на упаковке — нельзя в принципе, а посмотреть надо мимоходом,
 * не теряя места в списке. Щелчок ведёт на карточку Wildberries:
 * из фотографии всегда хотят перейти к товару.
 */

/**
 * Корзины, сработавшие по диапазонам номеров.
 *
 * ПО ДИАПАЗОНУ, А НЕ НА ВСЮ СТРАНИЦУ: товары одного диапазона лежат
 * вместе, далёкие — нет, и одна общая корзина сбивала перебор
 * у всех остальных.
 */
const memo = new Map<number, number>();

const BOX = { w: 220, h: 293 };

type ThumbProps = { nm: number; size?: number; height?: number; title?: string };
export function Thumb(props: ThumbProps) {
  if (!Number.isSafeInteger(props.nm) || props.nm <= 0) return null;
  return <ThumbImage key={props.nm} {...props} />;
}

function ThumbImage({
  nm,
  size = 34,
  height,
  title,
}: {
  nm: number;
  size?: number;
  /**
   * Высота, если она не равна ширине.
   *
   * ТОВАР ДРУГОГО ЗАКАЗА В ИТОГЕ РАЗБОРА — 30 × 38, а не квадрат
   * (передача 09.09.2026, раздел 7.2): в строке итога миниатюра стоит
   * одна, без имени рядом, и вытянутая по вертикали она читается как
   * фотография товара, а квадратная — как значок.
   */
  height?: number;
  title?: string;
}) {
  const startupWorkStarted = beginStartupWork();
  const h = height ?? size;
  const box = useRef<HTMLSpanElement>(null);
  const hosts = useRef(hostsFor(nm, memo.get(bucketOf(nm))));
  const [at, setAt] = useState<{ host: number; jpg: boolean } | null>({
    host: hosts.current[0]!,
    jpg: false,
  });
  const [loaded, setLoaded] = useState(false);
  const [zoom, setZoom] = useState<{ left: number; top: number } | null>(null);

  // После исчерпания адресов сохраняем размер ячейки и даём повторить загрузку.
  if (!at) return profileStartup('thumb.children', finishStartupWork('thumb.render', startupWorkStarted, <button type="button" aria-label={`Повторить загрузку фото ${nm}`} title="Фото не загрузилось. Повторить" className="rk-thumb inline-flex items-center justify-center" style={{ width: size, height: h, flex: '0 0 auto', color: 'var(--ink-3)' }} onClick={e=>{e.stopPropagation();setLoaded(false);setAt({host:hosts.current[0]!,jpg:false});}}><IconRefresh size={14}/></button>));

  const open = () => {
    const r = box.current?.getBoundingClientRect();
    if (!r || !loaded) return;
    const right = r.right + 12 + BOX.w < window.innerWidth;
    setZoom({
      left: right ? r.right + 12 : Math.max(8, r.left - BOX.w - 12),
      top: Math.min(Math.max(8, r.top - 40), window.innerHeight - BOX.h - 8),
    });
  };

  return profileStartup('thumb.children', finishStartupWork('thumb.render', startupWorkStarted, (
    <span
      ref={box}
      className="inline-block"
      style={{ width: size, height: h, flex: '0 0 auto', position: 'relative' }}
      onMouseEnter={open}
      onMouseLeave={() => setZoom(null)}
    >
      <a
        href={cardUrl(nm)}
        target="_blank"
        rel="noopener"
        title={title ?? `Открыть товар ${nm} на Wildberries`}
        onClick={(e) => e.stopPropagation()}
      >
        {!loaded && <span aria-hidden="true" className="rk-thumb inline-flex items-center justify-center" style={{position:'absolute',inset:0,width:size,height:h,color:'var(--ink-3)'}}><IconPhoto size={14}/></span>}
        <img
          src={photoUrl(nm, at.host, 'c246x328', at.jpg)}
          alt=""
          loading="lazy"
          /* Размер приходит снаружи: в строке 32, в карточке 40.
             Всё остальное — рамка, радиус, фон под незагруженной
             картинкой — из `rk-thumb` образца. */
          className="rk-thumb"
          style={{ width: size, height: h }}
          onLoad={() => {setLoaded(true);memo.set(bucketOf(nm), at.host);}}
          onError={() => setAt(nextAttempt(at, hosts.current))}
        />
      </a>

      {zoom && <Zoomed nm={nm} host={at.host} jpg={at.jpg} at={zoom} />}
    </span>
  )));
}

/**
 * Увеличенная картинка поверх страницы.
 *
 * БЕЗ ПЕРЕХВАТА МЫШИ (`pointer-events: none`): иначе курсор оказывается
 * над увеличением, а не над картинкой, наведение теряется — и оно мигает.
 */
function Zoomed({
  nm,
  host,
  jpg,
  at,
}: {
  nm: number;
  host: number;
  jpg: boolean;
  at: { left: number; top: number };
}) {
  const [big, setBig] = useState(true);
  useEffect(() => setBig(true), [nm]);

  return createPortal(
    <span
      data-kab
      className="pointer-events-none fixed z-[1000] rounded p-1"
      style={{
        left: at.left,
        top: at.top,
        background: 'var(--bg-surface)',
        border: '1px solid var(--line)',
        boxShadow: '0 12px 40px rgba(0,0,0,.22)',
      }}
    >
      <img
        src={photoUrl(nm, host, big ? 'big' : 'c246x328', jpg)}
        alt=""
        style={{ width: BOX.w, height: BOX.h, objectFit: 'cover', display: 'block', borderRadius: 4 }}
        // Крупного размера у части карточек нет — тогда показываем тот же,
        // что в строке: лучше мелкое, чем пустое место.
        onError={() => setBig(false)}
      />
    </span>,
    document.body,
  );
}

/**
 * Полоска фотографий кампании и число товаров.
 *
 * БОЛЬШЕ ЧЕТЫРЁХ КАРТИНОК В СТРОКУ НЕ ПОМЕЩАЕТСЯ, и показывать больше
 * незачем: число рядом говорит, сколько всего. Кампания с одним товаром
 * и кампания с двадцатью — разные звери, и различать их надо с одного
 * взгляда.
 *
 * ОБРЕЗАЕТ САМА, А НЕ ПОЛАГАЕТСЯ НА ВЫЗЫВАЮЩЕГО. Прежде обрезал сервер,
 * отдавая по четыре номера на кампанию, — и поиск по артикулу нашёл бы
 * только первые четыре товара, молча не найдя остальных. Теперь приходит
 * весь состав, а «сколько влезает в строку» — забота строки.
 */
/**
 * Сколько картинок в полоске.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * В ТАБЛИЦЕ ЧЕТЫРЕ, НА КАРТОЧКЕ ТЕЛЕФОНА ТРИ — ТАК В ЭТАЛОНЕ.
 *
 * У стенда это два разных счёта: `thumbs: min(nProd, 4)` в строке
 * таблицы и `thumbsM: min(nProd, 3)` на карточке. У нас стояло четыре
 * и там, и там: на телефоне полоска выходила на одну картинку длиннее
 * эталонной, а счётчик рядом — на единицу меньше («+2» вместо «+3»).
 * Сверка молчит: у полоски нет имени в эталоне.
 * ═══════════════════════════════════════════════════════════════════════
 */
const SHOWN = 4;

export function ProductStrip({
  nmIds,
  count,
  size = 30,
  показать = SHOWN,
}: {
  nmIds: number[];
  count: number;
  size?: number;
  /** Сколько картинок показать: 4 в таблице, 3 на карточке телефона. */
  показать?: number;
}) {
  const shown = nmIds.slice(0, показать);
  if (count === 0) {
    return (
      <span className="rk-text-sm whitespace-nowrap" style={{ color: 'var(--bad-ink)' }}>
        нет товаров
      </span>
    );
  }

  // РАЗМЕР И ЗАЗОР — ИЗ ЭТАЛОНА: тридцать пикселей и три между ними.
  // Стояло 34 и 4: четыре такие картинки со счётчиком не помещались
  // в столбец шириной 108 и переносились по одной — отсюда и «сплюснуто».
  // Переносить полоску нельзя вовсе: она должна оставаться полоской.
  return (
    <span className="flex flex-nowrap items-center" style={{ gap: 3 }}>
      {shown.map((nm) => (
        <Thumb key={nm} nm={nm} size={size} />
      ))}
      {count > shown.length && (
        <span className="rk-text-xs whitespace-nowrap rk-faint">+{count - shown.length}</span>
      )}
    </span>
  );
}
